import React, { useState, useEffect, useCallback } from "react";
import { getRequest, postRequest } from "../../utils/api";
import Loader from "../../Component/Loader/Loader";
import Pill from "../../Component/Pill";
import RowMenu, { RowMenuItem } from "../../Component/RowMenu";
import { logout } from "../../utils/auth";
import { inr } from "../../utils/staticData";
import { toast } from "react-toastify";
import PlanLayout, { PlanStats, lakh } from "./components/PlanLayout";
import PlanDrawer from "./components/PlanDrawer";

interface Plan {
  _id: string;
  ppm_plan_title: string;
  ppm_plan_description?: string;
  ppm_plan_monthly_price: number;
  ppm_plan_yearly_price: number;
  ppm_plan_year_discount?: number;
  ppm_plan_trial_days?: number;
  ppm_plan_visibility: "hidden" | "credit_line" | "own_card" | "both";
  ppm_plan_badge?: string;
  ppm_plan_is_black_card?: boolean;
  ppm_total_subscriptions: number;
  createdAt: string;
}

interface PaginationData {
  totalRecords: number;
  limit: number;
  currentPage: number;
  totalPages: number;
}

// Same rule the plan drawer uses to recover the pricing model from the two prices
const planType = (p: Plan) =>
  !p.ppm_plan_monthly_price && !p.ppm_plan_yearly_price ? "free" : !p.ppm_plan_yearly_price ? "lifetime" : "recurring";

const Plans: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; plan: Plan } | null>(null);
  const [stats, setStats] = useState<PlanStats>({});
  const [pagination, setPagination] = useState<PaginationData>({ totalRecords: 0, limit: 10, currentPage: 1, totalPages: 1 });
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const response: any = await getRequest(`/plans/list?page=${page}&status=${statusFilter}`);
      if (response.status === "success") {
        setPlans(response.data);
        setPagination(response.pagination);
        if (response.stats) setStats(prev => ({ ...prev, ...response.stats }));
      } else if (response.status === "session_expired") {
        logout();
      } else {
        toast.error(response.message || "Failed to fetch plans.");
      }
    } catch (error: any) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleOpenDrawer = (plan?: Plan) => {
    setSelectedPlan(plan || null);
    setShowDrawer(true);
  };

  const handleMakeDraft = async (planId: string) => {
    if (!window.confirm("Hide this plan from every pricing page?")) return;
    setLoading(true);
    try {
      const response: any = await postRequest(`/plans/update-status/${planId}`, { visibility: "hidden" });
      if (response.status === "success") {
        toast.success("Plan hidden.");
        fetchPlans();
      } else {
        toast.error(response.message || "Failed to update plan status.");
      }
    } catch (error: any) {
      toast.error(error.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const menuItems = (plan: Plan): RowMenuItem[] => {
    const items: RowMenuItem[] = [{ icon: "ri-edit-line", label: "Edit plan", onClick: () => handleOpenDrawer(plan) }];
    if (plan.ppm_plan_visibility !== "hidden") items.push("divider", { icon: "ri-archive-line", label: "Hide plan", onClick: () => handleMakeDraft(plan._id), danger: true });
    return items;
  };

  const closeMenu = useCallback(() => setMenu(null), []);
  const { totalPages, currentPage } = pagination;

  return (
    <>
      {loading && <Loader />}
      <PlanLayout
        stats={stats}
        actions={<>
          <select className="cx-select" style={{ height: 38 }} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="all">All plans</option>
            <option value="both">Both portals</option>
            <option value="credit_line">Credit-line only</option>
            <option value="own_card">Own-card only</option>
            <option value="hidden">Hidden</option>
          </select>
          <div className="cx-seg">
            <button type="button" className={billingCycle === "monthly" ? "on" : ""} onClick={() => setBillingCycle("monthly")}>Monthly</button>
            <button type="button" className={billingCycle === "yearly" ? "on" : ""} onClick={() => setBillingCycle("yearly")}>Yearly</button>
          </div>
          <button type="button" className="cx-btn cx-btn-primary" onClick={() => handleOpenDrawer()}><i className="ri-add-line"></i>New plan</button>
        </>}
      >
        <div className="cx-grid g3">
          {plans.map((plan) => {
            const type = planType(plan);
            const m = plan.ppm_plan_monthly_price || 0;
            const y = plan.ppm_plan_yearly_price || 0;
            const yearly = billingCycle === "yearly" && type === "recurring" && y > 0;
            const price = type === "free" ? 0 : yearly ? Math.round(y / 12) : m;
            const suffix = type === "lifetime" ? "/ one-time" : type === "free" ? "/ forever" : "/ month";
            const save = m > 0 && y > 0 ? Math.round((1 - y / (m * 12)) * 100) : plan.ppm_plan_year_discount || 0;
            const subs = plan.ppm_total_subscriptions || 0;
            const black = !!plan.ppm_plan_is_black_card;
            const live = plan.ppm_plan_visibility !== "hidden";
            const feats: [string, boolean][] = [
              [plan.ppm_plan_trial_days ? `${plan.ppm_plan_trial_days}-day free trial` : "No free trial", !!plan.ppm_plan_trial_days],
              [type === "recurring" ? `Yearly billing${save > 0 ? ` · save ${save}%` : ""}` : type === "lifetime" ? "One-time payment" : "Free forever", type !== "free"],
              [live ? "Shown on pricing page" : "Hidden from pricing page", live],
            ];

            return (
              <div className={`cx-plan-card ${black ? "black" : ""}`} key={plan._id}>
                <span className="bdg">
                  {plan.ppm_plan_visibility === "hidden" && <Pill tone="bad">Hidden</Pill>}
                  {plan.ppm_plan_visibility === "credit_line" && <Pill tone="none">Credit-line only</Pill>}
                  {plan.ppm_plan_visibility === "own_card" && <Pill tone="none">Own-card only</Pill>}
                  {plan.ppm_plan_badge && <Pill tone={black ? "warn" : "blue"}>{plan.ppm_plan_badge}</Pill>}
                </span>
                <h5>{plan.ppm_plan_title}</h5>
                <div className="muted fs12">{plan.ppm_plan_description || " "}</div>
                <div className="price">{inr(price)} <small>{suffix}</small></div>
                {yearly
                  ? <div className="fs12 up">Billed {inr(y)} yearly{save > 0 ? ` · save ${save}%` : ""}</div>
                  : <div className="fs12">&nbsp;</div>}
                <ul>
                  {feats.map(([label, on]) => (
                    <li key={label}><i className={on ? "ri-check-line" : "ri-close-line"}></i>{label}</li>
                  ))}
                </ul>
                <div className="statser4">
                  <div><span className="muted">Subscribers</span><b>{subs.toLocaleString("en-IN")}</b></div>
                  <div><span className="muted">MRR</span><b>{type === "recurring" ? lakh(subs * m) : "—"}</b></div>
                  <div className="acts">
                    <button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={() => handleOpenDrawer(plan)}><i className="ri-edit-line"></i>Edit</button>
                    <button type="button" className="cx-btn cx-btn-light cx-btn-xs" title="More" onClick={(e) => setMenu({ anchor: e.currentTarget, plan })}><i className="ri-more-2-fill"></i></button>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="cx-plan-card add" role="button" tabIndex={0} onClick={() => handleOpenDrawer()} onKeyDown={(e) => { if (e.key === "Enter") handleOpenDrawer(); }}>
            <i className="ri-add-circle-line"></i>
            <div style={{ marginTop: 6, fontWeight: 500 }}>Create new plan</div>
            <div className="fs12">Free, monthly/yearly or lifetime</div>
          </div>
        </div>

        {!loading && plans.length === 0 && (
          <div className="cx-card mt"><div className="cx-empty"><i className="ri-price-tag-3-line"></i>No plans found. Adjust the status filter or create a new plan.</div></div>
        )}

        {totalPages > 1 && (
          <div className="cx-card mt">
            <div className="cx-tfoot" style={{ borderTop: "none" }}>
              <span>Showing <b>{plans.length}</b> of <b>{pagination.totalRecords}</b> plans</span>
              <div className="cx-pager">
                <button type="button" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button type="button" key={p} className={p === currentPage ? "on" : ""} onClick={() => setPage(p)}>{p}</button>
                ))}
                <button type="button" disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}>›</button>
              </div>
            </div>
          </div>
        )}

        <RowMenu anchor={menu?.anchor || null} title={menu?.plan.ppm_plan_title} items={menu ? menuItems(menu.plan) : []} onClose={closeMenu} />
        <PlanDrawer
          show={showDrawer}
          planData={selectedPlan}
          handleClose={() => setShowDrawer(false)}
          refreshPlans={fetchPlans}
          onMakeDraft={handleMakeDraft}
        />
      </PlanLayout>
    </>
  );
};

export default Plans;
